import { INVOICING_SUMMARY_STATUS } from 'interfaces/invoices'
import { decodeBrackets } from './utils/decodeBrackets'

export type InvoicingPayload = {
  page?: number
  itemsPerPage?: number
  filters?: {
    statuses?: INVOICING_SUMMARY_STATUS[]
    reference?: string | null
    companies: number[]
    dateFrom?: string | null
    dateTo?: string | null
  }
}

export function getInvoicingSummaries({
  page = 1,
  itemsPerPage = 30,
  filters = { statuses: [], reference: null, companies: [], dateFrom: null, dateTo: null },
}: InvoicingPayload = {}): {
  url: string
  method: 'GET'
  headers: {
    Accept: 'application/ld+json'
    Authorization: null
  }
} {
  const params = new URLSearchParams()

  if (filters.statuses && filters.statuses.length > 0) {
    for (const status of filters.statuses) {
      params.append('status[]', status)
    }
  }

  if (filters.companies && filters.companies.length > 0) {
    for (const company of filters.companies) {
      params.append('companyId[]', company.toString())
    }
  }

  if (filters.reference) {
    params.append('reference', filters.reference)
  }

  if (filters.dateFrom) {
    params.append('dateFrom', filters.dateFrom)
  }

  if (filters.dateTo) {
    params.append('dateTo', filters.dateTo)
  }

  params.append('page', page.toString())
  params.append('itemsPerPage', itemsPerPage.toString())

  const formattedParams = decodeBrackets(params.toString())

  return {
    url: `/api/invoicing_summaries?${formattedParams}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function getInvoiceById(id: number) {
  return {
    url: `/api/invoicing_summaries/${id}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export function uploadInvoice({ params }) {
  const formData = new FormData()
  formData.append('invoicing_group[invoice][file][file]', params.body.values.invoice)

  return {
    url: `/api/invoicing_summaries/${params.id}/interface`,
    method: 'POST',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
    body: formData,
  }
}

export function downloadInvoice({ params }) {
  return {
    url: `/api/invoicing_summaries/${params.id}/download_invoice`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}

export type TransitionPayload = {
  id: number
  transitionName: 'reject' | 'validate'
  payload?: { emailBody?: string }
}

export function transitionInvoice({ id, transitionName, payload }: TransitionPayload) {
  const body = { email_body: payload?.emailBody } || {}
  return {
    url: `/api/invoicing_summaries/${id}/${transitionName}`,
    method: 'PATCH',
    headers: {
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
      Authorization: null,
    },
    body: JSON.stringify(body),
  }
}

export function getInvoicingTotals(
  filters = { statuses: [], reference: null, companies: [], dateFrom: null, dateTo: null },
): {
  url: string
  method: 'GET'
  headers: {
    Accept: 'application/ld+json'
    Authorization: null
  }
} {
  const params = new URLSearchParams()

  if (filters.statuses?.length) {
    for (const status of filters.statuses) {
      params.append('status[]', status)
    }
  }

  if (filters.companies?.length) {
    for (const company of filters.companies) {
      params.append('companyId[]', company.toString())
    }
  }

  if (filters.reference) {
    params.append('reference', filters.reference)
  }

  if (filters.dateFrom) {
    params.append('dateFrom', filters.dateFrom)
  }

  if (filters.dateTo) {
    params.append('dateTo', filters.dateTo)
  }

  const formattedParams = decodeBrackets(params.toString())

  return {
    url: `/api/invoicing_summaries/totals?${formattedParams}`,
    method: 'GET',
    headers: {
      Accept: 'application/ld+json',
      Authorization: null,
    },
  }
}
